import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Flame, Video, Crown } from "lucide-react";
import { youtubeEmbed } from "../../lib/video";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export const LockerPost = ({ post, onFire, busy }) => {
  const embed = youtubeEmbed(post.video_url);
  return (
    <motion.article data-testid={`locker-post-${post.id}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden border border-[#D4AF37]/25" style={{ background: "linear-gradient(160deg, rgba(212,175,55,0.08), rgba(12,12,12,0.85))" }}>
      {embed ? (
        <div className="relative w-full border-b border-[#D4AF37]/20" style={{ aspectRatio: "16 / 9" }}>
          <iframe title={post.title} src={embed} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      ) : post.video_url ? (
        <a href={post.video_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-3 border-b border-[#D4AF37]/20 text-xs font-bold uppercase tracking-wider text-[#D4AF37] hover:underline"><Video size={14} /> Watch video</a>
      ) : null}
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]/80">Coach Barker · {fmt(post.created_at)}</span>
          <button data-testid={`locker-fire-${post.id}`} onClick={() => onFire(post)} disabled={busy}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all active:scale-95 ${post.fired ? "bg-[#C41E24] border-[#C41E24] text-white shadow-[0_0_14px_rgba(196,30,36,0.6)]" : "border-white/15 text-white/60 hover:border-[#C41E24]/60 hover:text-white"}`}>
            <Flame size={13} className={post.fired ? "text-[#F5C518]" : ""} /> {post.fire_count}
          </button>
        </div>
        <h3 className="font-headings text-lg sm:text-xl uppercase tracking-wide leading-tight">{post.title}</h3>
        {post.caption && <p className="text-sm text-white/65 mt-2 whitespace-pre-line">{post.caption}</p>}
      </div>
    </motion.article>
  );
};

const Header = ({ muted }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: `${GOLD}26`, boxShadow: muted ? "none" : `0 0 18px ${GOLD}55` }}>
      <Lock size={16} style={{ color: GOLD }} />
    </span>
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: muted ? "rgba(255,255,255,0.4)" : GOLD }}>Elite Only · The Feed</div>
      <h2 className={`font-headings text-2xl uppercase tracking-wide ${muted ? "text-white/70" : ""}`}>Private Locker Room</h2>
    </div>
  </div>
);

// Elite-only video tips feed posted by the coach; non-Elite see a locked upsell teaser.
export default function LockerRoomFeed({ token, isElite, onLockedClick }) {
  const [posts, setPosts] = useState(null);
  const [busy, setBusy] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!isElite || !token) return;
    axios.get(`${API}/locker-posts`, auth).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
    // eslint-disable-next-line
  }, [isElite, token]);

  const fire = async (post) => {
    setBusy(post.id);
    try {
      const { data } = await axios.post(`${API}/locker-posts/${post.id}/fire`, {}, auth);
      setPosts((ps) => ps.map((p) => (p.id === data.id ? data : p)));
    } catch { /* ignore */ } finally { setBusy(""); }
  };

  if (!isElite) {
    return (
      <motion.button data-testid="locker-room-locked" onClick={onLockedClick} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="mt-10 w-full text-left rounded-2xl border border-white/10 bg-[#141414] p-5 sm:p-6 hover:border-[#D4AF37]/40 transition-colors">
        <Header muted />
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-white/15 bg-[#0d0d0d] px-4 py-6 justify-center">
          <Crown size={18} className="text-[#D4AF37]" />
          <span className="text-sm text-white/55">Elite members get Coach Barker's private video tips every week. <span className="font-semibold text-white/80 underline decoration-[#D4AF37]/50">Unlock with Elite</span></span>
        </div>
      </motion.button>
    );
  }

  return (
    <section data-testid="locker-room-feed" className="mt-10">
      <Header />
      {posts === null ? (
        <p className="text-sm text-white/40">Opening the locker room…</p>
      ) : posts.length === 0 ? (
        <div data-testid="locker-room-empty" className="rounded-2xl border border-dashed border-[#D4AF37]/25 bg-[#0d0d0d]/60 px-5 py-8 text-center text-sm text-white/45">Coach Barker's first Elite-only tip drops here soon. Keep training.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {posts.map((p) => <LockerPost key={p.id} post={p} onFire={fire} busy={busy === p.id} />)}
        </div>
      )}
    </section>
  );
}
